import type { ConfigItem } from '../../common/types'
import type { ConfigParams, MethodParams, Params } from '../params'
import { fieldToArg, fieldToConfigItem, fieldToParam, propertiesOf } from './field-mapper'
import { opaqueParam } from './method-model'
import type { SharedExtensionMetaInfoArg, SharedExtensionParam } from './model-types'

// The v1 output model of a declared schema — `bodyParams`, `metaInfo.args`, `ConfigItem[]`.
//
// These are free functions rather than methods on `Params` on purpose: the params framework is consumed by
// the console as well as the runner (it renders and validates the same schemas in the browser), and the v1
// console model is meaningless there. So the framework describes a schema, and this module — which belongs
// to the runner's output model — projects that description into the shapes v1 consumers expect.

// Top-level params: structure only. Display metadata is published through `toArgs()`.
export function toBodyParams(params: MethodParams): SharedExtensionParam[] {
  return params.fields().map(([name, schema]) => fieldToParam(name, schema))
}

// `metaInfo.args` — the display half of the same fields.
export function toArgs(params: MethodParams): Record<string, SharedExtensionMetaInfoArg> {
  return Object.fromEntries(params.fields().map(([name, schema]) => [name, fieldToArg(name, schema)]))
}

/**
 * Top-level params rendered OPAQUELY: one entry per declared key, typed from its schema, with no recursion
 * into nested properties and nothing ever marked required.
 *
 * This is the SYSTEM seam's model shape. Those methods are invoked by the runtime with a whole invocation
 * object, never through a rendered form, so publishing their internals would be noise — and the v1 seam they
 * must stay compatible with declares them exactly this way.
 */
export function toOpaqueBodyParams(params: MethodParams): SharedExtensionParam[] {
  return params.fields().map(([name, schema]) => opaqueParam(name, fieldToParam(name, schema).type))
}

// The same fields as NESTED properties: display metadata inlined on each one. Used where the schema
// describes the inside of a wrapper param rather than a method's top level (e.g. a dictionary's `criteria`
// inside its `payload`).
export function toProperties(params: Params): SharedExtensionParam[] {
  return propertiesOf(params.scheme as Parameters<typeof propertiesOf>[0])
}

// The config descriptions the console renders. `.shared()` fields are marked shared (→
// sharedConfigDescriptions downstream); the rest are per-app.
export function toConfigItems(config: ConfigParams): ConfigItem[] {
  return config.fields().map(([name, schema]) => fieldToConfigItem(name, schema))
}
